import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, ChevronLeft, ChevronRight, Loader2, Minus, X } from "lucide-react";
import type { Property } from "@/types/property";
import { api } from "@/lib/api";
import { formatArea, formatBedrooms, formatPrice } from "@/lib/format";
import { useCompare } from "@/lib/compare-context";
import { SectionHeading } from "@/components/site/SectionHeading";
import { SafeImage } from "@/components/site/SafeImage";
import { rankPropertyImages } from "@/lib/property-images";
import { propertyPath } from "@/lib/property-url";
import { useSeo } from "@/hooks/useSeo";

export function ComparePage() {
  useSeo({
    title: "Compare Properties | Hive Homes Dubai",
    description:
      "Compare shortlisted Dubai homes side by side with Hive Homes.",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { ids: ctxIds, remove } = useCompare();

  const urlIds = useMemo(() => {
    const raw = searchParams.get("ids") ?? "";
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [searchParams]);

  // Source of truth: URL when present, otherwise context (so deep-links work).
  const ids = urlIds.length > 0 ? urlIds : ctxIds;

  const [properties, setProperties] = useState<Record<string, Property>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(
      ids.map((id) =>
        api.getProperty(id).then(
          (p) => [id, p] as const,
          (e) => {
            console.error(e);
            return null;
          },
        ),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const next: Record<string, Property> = {};
        for (const r of results) {
          if (r) next[r[0]] = r[1];
        }
        setProperties(next);
      })
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ids.join(",")]);

  const items = ids.map((id) => properties[id]).filter((p): p is Property => Boolean(p));

  const allAmenities = useMemo(() => {
    const s = new Set<string>();
    for (const p of items) for (const a of p.amenities) s.add(a);
    return [...s].sort();
  }, [items]);

  const allFeatures = useMemo(() => {
    const s = new Set<string>();
    for (const p of items) for (const f of p.features) s.add(f);
    return [...s].sort();
  }, [items]);

  const handleRemove = (id: string) => {
    remove(id);
    const next = ids.filter((x) => x !== id);
    if (next.length === 0) {
      setSearchParams(new URLSearchParams());
    } else {
      const sp = new URLSearchParams();
      sp.set("ids", next.join(","));
      setSearchParams(sp);
    }
  };

  if (loading) {
    return (
      <div className="container-x flex h-[60vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-[var(--cream)]" />
      </div>
    );
  }

  if (ids.length === 0) {
    return (
      <section className="container-wide py-20 md:py-28 short:py-10">
        <SectionHeading
          eyebrow="Side by side"
          title="Pick a few homes to compare."
          body="Tap the + on any property to add it here. We'll line them up so you can spot the difference at a glance."
          as="h1"
          size="xl"
        />
        <Link to="/properties" className="btn btn-primary mt-8 w-fit">
          Browse properties
        </Link>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="container-wide py-20 text-center">
        <p className="font-display text-[32px] text-[var(--text)]">We couldn't load any of these.</p>
        <p className="mt-3 text-[var(--text-soft)]">
          {error ?? "Try removing them and adding fresh listings."}
        </p>
        <Link to="/properties" className="btn btn-ghost mt-6">
          <ArrowLeft className="size-4" />
          Back to properties
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
        <div className="container-wide py-10 md:py-14 short:py-8">
          <Link
            to="/properties"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--text-muted)] hover:text-[var(--cream)]"
          >
            <ArrowLeft className="size-3.5" />
            Back to properties
          </Link>
          <h1
            className="mt-6 font-display text-[36px] leading-[1] text-[var(--text)] sm:text-[48px] md:text-[72px]"
            style={{ fontVariationSettings: '"opsz" 96' }}
          >
            Side by side.
          </h1>
          <p className="mt-4 max-w-[60ch] text-[15px] leading-[1.6] text-[var(--text-soft)] md:text-[16.5px]">
            {items.length} {items.length === 1 ? "home" : "homes"} laid out for the eye to compare. Remove any to refine your shortlist.
          </p>
        </div>
      </section>

      <section className="container-wide pb-24 pt-6 md:pt-10">
        <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[720px] border-separate border-spacing-0 text-left text-[14px]">
            <colgroup>
              <col className="w-[140px] sm:w-[180px]" />
              {items.map((p) => (
                <col key={p.id} />
              ))}
            </colgroup>

            {/* Photo + remove + title row */}
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-[var(--bg)] align-bottom" />
                {items.map((p) => {
                  return (
                    <th
                      key={p.id}
                      className="border-b border-[var(--line)] p-3 align-top sm:p-4"
                    >
                    <div className="relative">
                      <CompareCover property={p} />
                      <button
                        type="button"
                        onClick={() => handleRemove(p.id)}
                        aria-label={`Remove ${p.title}`}
                        className="absolute right-2 top-2 inline-flex size-10 items-center justify-center bg-[var(--navy)]/90 text-[var(--cream)] backdrop-blur-sm transition-colors hover:bg-[var(--cream)] hover:text-[var(--navy)] sm:size-8"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                    <p className="mt-3 text-[10.5px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                      {p.community}{p.subCommunity ? ` · ${p.subCommunity}` : ""}
                    </p>
                    <Link to={propertyPath(p)}>
                      <p
                        className="mt-1 font-display text-[18px] font-semibold leading-tight text-[var(--text)] hover:text-[var(--cream)] sm:text-[20px]"
                        style={{ fontVariationSettings: '"opsz" 36' }}
                      >
                        {p.title}
                      </p>
                    </Link>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              <Row label="Price">
                {items.map((p) => (
                  <Cell key={p.id}>
                    <span
                      className="font-display text-[20px] font-semibold text-[var(--cream)] sm:text-[22px]"
                      style={{ fontVariationSettings: '"opsz" 36' }}
                    >
                      {formatPrice(p.price, p.pricePeriod)}
                    </span>
                  </Cell>
                ))}
              </Row>

              <Row label="Listing">
                {items.map((p) => (
                  <Cell key={p.id}>
                    <span className={p.isExclusive ? "badge badge-gold" : "badge badge-navy"}>
                      {p.isExclusive
                        ? "Exclusive"
                        : p.listingType === "off-plan"
                          ? "Off-plan"
                          : p.listingType === "rent"
                            ? "For rent"
                            : "For sale"}
                    </span>
                  </Cell>
                ))}
              </Row>

              <Row label="Type">
                {items.map((p) => (
                  <Cell key={p.id} className="capitalize">{p.propertyType}</Cell>
                ))}
              </Row>

              <Row label="Bedrooms">
                {items.map((p) => (
                  <Cell key={p.id}>{formatBedrooms(p.bedrooms)}</Cell>
                ))}
              </Row>

              <Row label="Bathrooms">
                {items.map((p) => (
                  <Cell key={p.id}>{p.bathrooms}</Cell>
                ))}
              </Row>

              <Row label="Built-up area">
                {items.map((p) => (
                  <Cell key={p.id}>{formatArea(p.area)}</Cell>
                ))}
              </Row>

              <Row label="Price / sqft">
                {items.map((p) => (
                  <Cell key={p.id} className="text-[var(--text-soft)]">
                    {p.area > 0 && p.price > 0
                      ? `AED ${Math.round(p.price / p.area).toLocaleString("en-AE")}`
                      : "-"}
                  </Cell>
                ))}
              </Row>

              <Row label="Parking">
                {items.map((p) => (
                  <Cell key={p.id}>{p.parking ?? "-"}</Cell>
                ))}
              </Row>

              <Row label="Community">
                {items.map((p) => (
                  <Cell key={p.id}>
                    {p.community}
                    {p.subCommunity && (
                      <span className="block text-[12px] text-[var(--text-muted)]">{p.subCommunity}</span>
                    )}
                  </Cell>
                ))}
              </Row>

              {items.some((p) => p.developer) && (
                <Row label="Developer">
                  {items.map((p) => (
                    <Cell key={p.id}>{p.developer ?? "-"}</Cell>
                  ))}
                </Row>
              )}

              {items.some((p) => p.handoverDate) && (
                <Row label="Handover">
                  {items.map((p) => (
                    <Cell key={p.id}>
                      {p.handoverDate
                        ? new Date(p.handoverDate).toLocaleDateString("en-AE", { month: "short", year: "numeric" })
                        : "-"}
                    </Cell>
                  ))}
                </Row>
              )}

              {items.some((p) => p.paymentPlan) && (
                <Row label="Payment plan">
                  {items.map((p) => (
                    <Cell key={p.id}>{p.paymentPlan ?? "-"}</Cell>
                  ))}
                </Row>
              )}

              {allAmenities.length > 0 && (
                <>
                  <SectionRow label="Amenities" colSpan={items.length + 1} />
                  {allAmenities.map((a) => (
                    <Row key={`am-${a}`} label={a}>
                      {items.map((p) => (
                        <Cell key={p.id} className="text-center">
                          {p.amenities.includes(a) ? (
                            <Check className="mx-auto size-4 text-[var(--cream)]" />
                          ) : (
                            <Minus className="mx-auto size-4 text-[var(--text-muted)]" />
                          )}
                        </Cell>
                      ))}
                    </Row>
                  ))}
                </>
              )}

              {allFeatures.length > 0 && (
                <>
                  <SectionRow label="Features" colSpan={items.length + 1} />
                  {allFeatures.map((f) => (
                    <Row key={`ft-${f}`} label={f}>
                      {items.map((p) => (
                        <Cell key={p.id} className="text-center">
                          {p.features.includes(f) ? (
                            <Check className="mx-auto size-4 text-[var(--cream)]" />
                          ) : (
                            <Minus className="mx-auto size-4 text-[var(--text-muted)]" />
                          )}
                        </Cell>
                      ))}
                    </Row>
                  ))}
                </>
              )}

              <SectionRow label="Your guide" colSpan={items.length + 1} />
              <Row label="Agent">
                {items.map((p) => (
                  <Cell key={p.id}>
                    <p className="text-[var(--text)]">{p.agent.name}</p>
                    {p.agent.title && (
                      <p className="text-[12px] text-[var(--text-muted)]">{p.agent.title}</p>
                    )}
                  </Cell>
                ))}
              </Row>

              <Row label="">
                {items.map((p) => (
                  <Cell key={p.id}>
                    <Link
                      to={propertyPath(p)}
                      className="inline-flex min-h-11 items-center gap-2 border border-[var(--line-strong)] px-3 py-2 text-[11.5px] uppercase tracking-[0.2em] text-[var(--text)] transition-colors hover:border-[var(--cream)] hover:text-[var(--cream)] lg:min-h-0"
                    >
                      View home
                      <ArrowUpRight className="size-3.5" />
                    </Link>
                  </Cell>
                ))}
              </Row>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/** Compared listing cover with the same 3-photo slider as the cards. */
function CompareCover({ property }: { property: Property }) {
  const slides = useMemo(
    () => rankPropertyImages(property.images, property.id).slice(0, 3),
    [property.images, property.id],
  );
  const [slide, setSlide] = useState(0);
  const goTo = (e: React.MouseEvent, next: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSlide(((next % slides.length) + slides.length) % slides.length);
  };

  return (
    <Link to={propertyPath(property)} className="group block">
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--surface-2)]">
        <div
          className="flex h-full w-full transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${slide * 100}%)` }}
        >
          {slides.map((src, i) => (
            <div key={src + i} className="h-full w-full flex-none">
              <SafeImage
                src={src}
                alt={i === 0 ? property.title : ""}
                loading="lazy"
                sizes="(min-width: 768px) 25vw, 50vw"
                widths={[320, 480, 640]}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>

        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => goTo(e, slide - 1)}
              aria-label="Previous photo"
              className="absolute left-1.5 top-1/2 z-[1] inline-flex size-9 -translate-y-1/2 items-center justify-center bg-[rgba(13,20,34,0.55)] text-white opacity-90 backdrop-blur-sm transition-opacity hover:bg-[rgba(13,20,34,0.8)] hoverable:sm:opacity-0 sm:size-7 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
            >
              <ChevronLeft className="size-4 sm:size-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={(e) => goTo(e, slide + 1)}
              aria-label="Next photo"
              className="absolute right-1.5 top-1/2 z-[1] inline-flex size-9 -translate-y-1/2 items-center justify-center bg-[rgba(13,20,34,0.55)] text-white opacity-90 backdrop-blur-sm transition-opacity hover:bg-[rgba(13,20,34,0.8)] hoverable:sm:opacity-0 sm:size-7 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
            >
              <ChevronRight className="size-4 sm:size-3.5" aria-hidden />
            </button>
            <span className="pointer-events-none absolute inset-x-0 bottom-2 z-[1] flex items-center justify-center gap-1.5">
              {slides.map((_, i) => (
                <span
                  key={i}
                  aria-hidden
                  className={
                    "size-1.5 rounded-full transition-colors " +
                    (i === slide ? "bg-white" : "bg-white/45")
                  }
                />
              ))}
            </span>
          </>
        )}
      </div>
    </Link>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr className="border-b border-[var(--line)]">
      <th
        scope="row"
        className="sticky left-0 z-[1] bg-[var(--bg)] py-3 pr-4 align-top text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)] sm:py-4"
      >
        {label}
      </th>
      {children}
    </tr>
  );
}

function Cell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <td
      className={
        "border-l border-[var(--line)] p-3 align-top text-[var(--text)] sm:p-4 " +
        (className ?? "")
      }
    >
      {children}
    </td>
  );
}

function SectionRow({ label, colSpan }: { label: string; colSpan: number }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="bg-[var(--surface)]/40 px-0 py-3 text-[10.5px] uppercase tracking-[0.22em] text-[var(--cream)] sm:py-4"
      >
        <span className="sticky left-0 inline-block">{label}</span>
      </td>
    </tr>
  );
}
